import {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useRef,
} from "react";

export interface FlowchartIframeProps {
  iframeSrc?: string;
  onExport?: (files: Record<string, string>) => void;
  hideOpenButton?: boolean;
}

export interface FlowchartIframeHandle {
  open: () => void;
  close: () => void;
  postMessage: (msg: any) => void;
}

const FlowchartIframe = forwardRef<FlowchartIframeHandle, FlowchartIframeProps>(
  (
    { iframeSrc = "/builder/index.html", onExport, hideOpenButton = false },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const iframeRef = useRef<HTMLIFrameElement>(null);

    // Expose API
    useImperativeHandle(
      ref,
      () => ({
        open: () => setIsOpen(true),
        close: () => setIsOpen(false),
        postMessage: (msg) =>
          iframeRef.current?.contentWindow?.postMessage(msg, "*"),
      }),
      []
    );

    // Listen for RETURN_FILES
    useEffect(() => {
      const handler = (e: MessageEvent) => {
        if (e.data?.type === "RETURN_FILES" && onExport) {
          onExport(e.data.payload);
        }
      };
      window.addEventListener("message", handler);
      return () => window.removeEventListener("message", handler);
    }, [onExport]);

    return (
      <>
        {/* Optional built‑in opener */}
        {!hideOpenButton && !isOpen && (
          <button
            onClick={() => setIsOpen(true)}
            className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-1 px-3 rounded"
          >
            Open Builder
          </button>
        )}

        {/* Backdrop (always in DOM, but hidden when closed) */}
        <div
          onClick={() => setIsOpen(false)}
          className={`fixed inset-0 bg-black bg-opacity-50 z-40 ${
            isOpen ? "block" : "hidden"
          }`}
        />

        {/* Container (always in DOM, but hidden when closed) */}
        <div
          className={`fixed inset-5 z-50 bg-white flex flex-col rounded shadow-lg ${
            isOpen ? "flex" : "hidden"
          }`}
        >
          <div className="p-2 bg-gray-100 flex justify-end">
            <button
              onClick={() => setIsOpen(false)}
              className="bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold py-1 px-3 rounded"
            >
              Close
            </button>
          </div>
          <iframe
            ref={iframeRef}
            src={iframeSrc}
            title="Flowchart Builder"
            className="flex-1 border-0"
          />
        </div>
      </>
    );
  }
);

export default FlowchartIframe;
